import { useState, useEffect, useRef, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { AlertTriangle, Plus, MoreVertical, Pencil, Trash2, Search, CheckCircle, Clock, Upload, FileText, Image, X, Plane, Battery, Eye, Download, Paperclip, Camera, MessageSquare, MapPin, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { getApiErrorMessage } from "../lib/apiErrors";

// Incident type options
const INCIDENT_TYPES = [
  "equipment_failure",
  "collision_risk",
  "loss_of_control",
  "airspace_violation",
  "pilot_error",
  "environmental",
  "flyaway",
  "injury",
  "property_damage",
  "other"
];


export const Incidents = () => {
  const { user } = useAuth();
  const { activeOrganizationId, isPlatformAdmin, registerScopeChangeGuard } = useOrganizationScope();
  const scopedOrganizationId = isPlatformAdmin ? activeOrganizationId : user?.organization_id;
  const { t, language, translate } = useLanguage();
  const [incidents, setIncidents] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [missions, setMissions] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [batteries, setBatteries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [editingIncident, setEditingIncident] = useState(null);
  const [resolvingIncident, setResolvingIncident] = useState(null);
  const [viewingIncident, setViewingIncident] = useState(null);
  const [correctiveActions, setCorrectiveActions] = useState("");
  const [selectedMission, setSelectedMission] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    organization_id: "",
    mission_reference_id: "",
    incident_date: "",
    incident_classification: "minor",
    severity_level: "low",
    incident_type: "",
    description: "",
    reporting_required: false,
    reportable_to_ucl: false,
    corrective_actions: "",
    equipment_type: "",
    equipment_involved: []
  });

  const fetchData = useCallback(async () => {
    try {
      const [incidentsRes, orgsRes, missionsRes, pilotsRes, aircraftRes, batteriesRes] = await Promise.all([
        api.get(`/incidents`),
        api.get(`/organizations`),
        api.get(`/missions`),
        api.get(`/pilots`),
        api.get(`/aircraft`),
        api.get(`/batteries`)
      ]);
      setIncidents(incidentsRes.data);
      setOrganizations(orgsRes.data);
      setMissions(missionsRes.data);
      setPilots(pilotsRes.data);
      setAircraft(aircraftRes.data);
      setBatteries(batteriesRes.data);
    } catch (error) {
      toast.error(t.messages.fetchError);
    } finally {
      setLoading(false);
    }
  }, [ t.messages.fetchError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!showDialog) return undefined;
    return registerScopeChangeGuard(() => window.confirm(t.organizationScope?.formDirtyConfirm));
  }, [showDialog, registerScopeChangeGuard, t.organizationScope?.formDirtyConfirm]);

  const handleMissionSelect = (missionRefId) => {
    if (missionRefId === "__none__") {
      setSelectedMission(null);
      setFormData(prev => ({
        ...prev,
        mission_reference_id: ""
      }));
      return;
    }
    
    const mission = missions.find(m => m.mission_id === missionRefId);
    setSelectedMission(mission);
    setFormData(prev => ({
      ...prev,
      mission_reference_id: missionRefId,
      incident_date: prev.incident_date || mission?.mission_date?.split("T")[0] || ""
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.incident_date) {
      toast.error(t.validation.selectIncidentDate);
      return;
    }
    if (!formData.description.trim()) {
      toast.error(t.validation.provideIncidentDescription);
      return;
    }
    
    const payload = {
      ...formData,
      organization_id: scopedOrganizationId,
      mission_reference_id: formData.mission_reference_id || null,
      equipment_involved: formData.equipment_involved.length > 0 ? formData.equipment_involved : null,
      equipment_type: formData.equipment_type || null
    };
    
    try {
      let incidentId;
      if (editingIncident) {
        await api.put(`/incidents/${editingIncident.id}`, payload);
        incidentId = editingIncident.id;
        toast.success(t.messages.incidentUpdated);
      } else {
        const res = await api.post(`/incidents`, payload);
        incidentId = res.data.id;
        toast.success(t.messages.incidentReported);
        
        // Upload attachments if any
        for (const file of attachments) {
          await uploadAttachment(incidentId, file);
        }
      }
      
      setShowDialog(false);
      setEditingIncident(null);
      setSelectedMission(null);
      setAttachments([]);
      resetForm();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.message_cs || detail?.message_en) {
        const lang = t.common?.language === 'cs' ? 'cs' : 'en';
        toast.error(lang === 'cs' ? detail.message_cs : detail.message_en);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error(t.messages.saveError);
      }
    }
  };

  const uploadAttachment = async (incidentId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await api.post(`/incidents/${incidentId}/attachments`, formData, {
        headers: {
          
          "Content-Type": "multipart/form-data"
        }
      });
    } catch (error) {
      // The incident is already saved and the dialog closes; attachments cannot be added later.
      const reason = getApiErrorMessage(error, language, null);
      const message = translate('safety.attachmentUploadFailed', { name: file.name });
      toast.error(reason ? `${message} ${reason}` : message);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleResolve = async () => {
    if (!correctiveActions.trim()) {
      toast.error(t.validation.provideCorrectiveActions);
      return;
    }
    
    try {
      await api.put(
        `/incidents/${resolvingIncident.id}/resolve`,
        null,
        { params: { corrective_actions: correctiveActions } }
      );
      toast.success(t.messages.incidentResolved);
      setShowResolveDialog(false);
      setResolvingIncident(null);
      setCorrectiveActions("");
      fetchData();
    } catch (error) {
      toast.error(t.messages.saveError);
    }
  };

  const handleDelete = async (incident) => {
    if (!window.confirm(`${t.actions.delete}?`)) return;
    try {
      await api.delete(`/incidents/${incident.id}`);
      toast.success(t.messages.incidentDeleted);
      fetchData();
    } catch (error) {
      toast.error(t.messages.deleteError);
    }
  };

  const handleEdit = (incident) => {
    setEditingIncident(incident);
    setFormData({
      organization_id: incident.organization_id,
      mission_reference_id: incident.mission_reference_id || "",
      incident_date: incident.incident_date?.split("T")[0] || "",
      incident_classification: incident.incident_classification,
      severity_level: incident.severity_level || "low",
      incident_type: incident.incident_type || "",
      description: incident.description,
      reporting_required: incident.reporting_required,
      reportable_to_ucl: incident.reportable_to_ucl || false,
      corrective_actions: incident.corrective_actions || "",
      equipment_type: incident.equipment_type || "",
      equipment_involved: incident.equipment_involved || []
    });
    if (incident.mission_reference_id) {
      const mission = missions.find(m => m.mission_id === incident.mission_reference_id);
      setSelectedMission(mission);
    }
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      organization_id: scopedOrganizationId || "",
      mission_reference_id: "",
      incident_date: new Date().toISOString().split("T")[0],
      incident_classification: "minor",
      severity_level: "low",
      incident_type: "",
      description: "",
      reporting_required: false,
      reportable_to_ucl: false,
      corrective_actions: "",
      equipment_type: "",
      equipment_involved: []
    });
    setSelectedMission(null);
    setAttachments([]);
    setPhotos([]);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.company_name || "-";
  };

  const getPilotName = (pilotId) => {
    const pilot = pilots.find(p => p.id === pilotId);
    return pilot?.name || "-";
  };

  const getAircraftName = (aircraftId) => {
    const ac = aircraft.find(a => a.id === aircraftId);
    return ac?.model || "-";
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case "critical": return <Badge className="bg-rose-600 text-white">{t.safety.critical}</Badge>;
      case "high": return <Badge className="bg-rose-100 text-rose-800">{t.safety.high}</Badge>;
      case "medium": return <Badge className="bg-amber-100 text-amber-800">{t.safety.medium}</Badge>;
      default: return <Badge className="bg-slate-100 text-slate-600">{t.safety.low}</Badge>;
    }
  };

  const handleViewIncident = (incident) => {
    setViewingIncident(incident);
    setShowDetailDialog(true);
  };

  const handleDownloadAttachment = async (incident, attachment) => {
    try {
      const response = await api.get(
        `/incidents/${incident.id}/attachments/${attachment.id}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t.messages.fetchError);
    }
  };

  const handlePreviewImage = async (incident, attachment) => {
    try {
      const response = await api.get(
        `/incidents/${incident.id}/attachments/${attachment.id}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: attachment.mime_type }));
      setPreviewImage({
        url,
        name: attachment.file_name,
        mime_type: attachment.mime_type
      });
      setShowImagePreview(true);
    } catch (error) {
      toast.error(t.messages.fetchError);
    }
  };

  const isImageFile = (mimeType) => {
    return mimeType && mimeType.startsWith('image/');
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getFileIcon = (mimeType) => {
    if (isImageFile(mimeType)) {
      return <Image className="h-5 w-5 text-sky-500" />;
    }
    return <FileText className="h-5 w-5 text-slate-500" />;
  };

  const getIncidentTypeLabel = (type) => {
    if (!type) return "-";
    const labels = t.safety?.incidentTypes || {};
    return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Photo upload handler
  const handlePhotoUpload = async (e, incidentId) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: ${t.safety?.maxPhotoSize || "Max 10MB"}`);
        continue;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name}: ${t.safety?.imagesOnly || "Only images allowed"}`);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        await api.post(`/incidents/${incidentId}/photos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success(`${t.safety?.uploadPhoto || "Photo uploaded"}`);
        // Refresh incident data
        const res = await api.get(`/incidents/${incidentId}`);
        setViewingIncident(res.data);
      } catch (error) {
        toast.error(`${file.name}: ${getApiErrorMessage(error, language, t.safety?.uploadFailed || "Upload failed")}`);
      }
    }
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  // Photo preview handler
  const handlePreviewPhoto = async (incident, photo) => {
    try {
      const response = await api.get(
        `/incidents/${incident.id}/photos/${photo.id}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: photo.mime_type }));
      setPreviewImage({
        url,
        name: photo.file_name,
        mime_type: photo.mime_type,
        metadata: photo
      });
      setShowImagePreview(true);
    } catch (error) {
      toast.error(t.messages?.fetchError || "Failed to load photo");
    }
  };

  // Delete photo handler
  const handleDeletePhoto = async (incident, photoId) => {
    if (!window.confirm(t.actions?.delete + "?")) return;
    try {
      await api.delete(`/incidents/${incident.id}/photos/${photoId}`);
      toast.success(t.safety?.photoDeleted || "Photo deleted");
      const res = await api.get(`/incidents/${incident.id}`);
      setViewingIncident(res.data);
    } catch (error) {
      toast.error(t.safety?.photoDeleteError || "Delete failed");
    }
  };

  // Add comment handler
  const handleAddComment = async (incidentId) => {
    if (!newComment.trim()) return;
    setSubmittingComment(true);
    try {
      await api.post(`/incidents/${incidentId}/comments`, { content: newComment });
      toast.success(t.safety?.addComment || "Comment added");
      setNewComment("");
      const res = await api.get(`/incidents/${incidentId}`);
      setViewingIncident(res.data);
    } catch (error) {
      toast.error(t.safety?.addCommentError || "Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  // Delete comment handler
  const handleDeleteComment = async (incidentId, commentId) => {
    if (!window.confirm(t.safety?.deleteComment + "?")) return;
    try {
      await api.delete(`/incidents/${incidentId}/comments/${commentId}`);
      toast.success(t.safety?.commentDeleted || "Comment deleted");
      const res = await api.get(`/incidents/${incidentId}`);
      setViewingIncident(res.data);
    } catch (error) {
      toast.error(t.safety?.commentDeleteError || "Delete failed");
    }
  };

  const filteredIncidents = incidents.filter(incident =>
    incident.description.toLowerCase().includes(search.toLowerCase()) ||
    (incident.mission_reference_id && incident.mission_reference_id.toLowerCase().includes(search.toLowerCase()))
  );

  const openCount = incidents.filter(i => i.resolution_status === "open").length;
  const seriousCount = incidents.filter(i => i.incident_classification === "serious").length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="incidents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {t.safety.incidents}
          </h1>
          <p className="text-slate-500 mt-1">{t.pageDescriptions.incidents}</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setEditingIncident(null); setShowDialog(true); }}
          className="gap-2 bg-primary hover:bg-primary/90"
          data-testid="report-incident-btn"
        >
          <Plus className="h-4 w-4" /> {t.safety.reportIncident}
        </Button>
      </div>

      {/* Alerts */}
      {(openCount > 0 || seriousCount > 0) && (
        <div className="flex gap-4">
          {openCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              <Clock className="h-4 w-4 text-amber-500" />
              <span className="text-sm text-amber-700">{openCount} {t.safety.openIncidentsAlert}</span>
            </div>
          )}
          {seriousCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-rose-50 border border-rose-200 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-rose-500" />
              <span className="text-sm text-rose-700">{seriousCount} {t.safety.seriousIncidentsAlert}</span>
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t.safety.searchIncidents}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-incidents"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.common.date}</TableHead>
                <TableHead>{t.safety.missionReference}</TableHead>
                {user?.role === "platform_admin" && <TableHead>{t.common.organization}</TableHead>}
                <TableHead>{t.safety.classification}</TableHead>
                <TableHead>{t.safety.severity}</TableHead>
                <TableHead>{t.safety.description}</TableHead>
                <TableHead>{t.common.status}</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : filteredIncidents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <AlertTriangle className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">{t.safety.noIncidentsFound}</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredIncidents.map((incident) => (
                  <TableRow key={incident.id} data-testid={`incident-row-${incident.id}`}>
                    <TableCell className="font-medium">{formatDate(incident.incident_date)}</TableCell>
                    <TableCell>
                      {incident.mission_reference_id ? (
                        <span className="font-mono text-sm text-sky-600">{incident.mission_reference_id}</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    {user?.role === "platform_admin" && (
                      <TableCell className="text-sm text-slate-500">{getOrgName(incident.organization_id)}</TableCell>
                    )}
                    <TableCell>
                      {incident.incident_classification === "serious" ? (
                        <Badge className="bg-rose-100 text-rose-800">{t.safety.serious}</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800">{t.safety.minor}</Badge>
                      )}
                    </TableCell>
                    <TableCell>{getSeverityBadge(incident.severity_level)}</TableCell>
                    <TableCell className="max-w-48 truncate">{incident.description}</TableCell>
                    <TableCell>
                      {incident.resolution_status === "resolved" ? (
                        <Badge className="bg-emerald-100 text-emerald-800 flex items-center gap-1 w-fit">
                          <CheckCircle className="h-3 w-3" /> {t.safety.resolved}
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 flex items-center gap-1 w-fit">
                          <Clock className="h-3 w-3" /> {t.safety.open}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`incident-actions-${incident.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewIncident(incident)}>
                            <Eye className="h-4 w-4 mr-2" /> {t.actions.view}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(incident)}>
                            <Pencil className="h-4 w-4 mr-2" /> {t.actions.edit}
                          </DropdownMenuItem>
                          {incident.resolution_status === "open" && (
                            <DropdownMenuItem onClick={() => { setResolvingIncident(incident); setShowResolveDialog(true); }}>
                              <CheckCircle className="h-4 w-4 mr-2" /> {t.safety.resolve}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDelete(incident)} className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" /> {t.actions.delete}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Report/Edit Incident Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingIncident ? t.dialogs.editIncident : t.dialogs.reportIncident}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="incident-form">
            {/* Mission Reference Section */}
            <Card className="border-sky-200 bg-sky-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {t.safety.missionReference}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{t.safety.selectMissionById}</Label>
                  <Select
                    value={formData.mission_reference_id || "__none__"}
                    onValueChange={handleMissionSelect}
                  >
                    <SelectTrigger data-testid="mission-select">
                      <SelectValue placeholder={t.safety.selectMission} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t.safety.noLinkedMission}</SelectItem>
                      {missions.map((m) => (
                        <SelectItem key={m.id} value={m.mission_id || m.id}>
                          {m.mission_id || "N/A"} - {m.location} ({formatDate(m.mission_date)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedMission && (
                  <div className="grid grid-cols-2 gap-4 p-3 bg-white rounded-lg border">
                    <div>
                      <p className="text-xs text-slate-500">{t.flightOps.missionDate}</p>
                      <p className="text-sm font-medium">{formatDate(selectedMission.mission_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">{t.flightOps.location}</p>
                      <p className="text-sm font-medium">{selectedMission.location}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">{t.flightOps.pilot}</p>
                      <p className="text-sm font-medium">{getPilotName(selectedMission.pilot_id)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">{t.flightOps.aircraft}</p>
                      <p className="text-sm font-medium">{getAircraftName(selectedMission.aircraft_id)}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Incident Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="incident_date">{t.safety.incidentDate} *</Label>
                <Input
                  id="incident_date"
                  type="date"
                  value={formData.incident_date}
                  onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })}
                  required
                  data-testid="incident-date"
                />
              </div>
              <div className="space-y-2">
                <Label>{t.safety.classification} *</Label>
                <Select
                  value={formData.incident_classification}
                  onValueChange={(value) => setFormData({ ...formData, incident_classification: value })}
                >
                  <SelectTrigger data-testid="classification-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minor">{t.safety.minor}</SelectItem>
                    <SelectItem value="serious">{t.safety.serious}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.safety.severity}</Label>
                <Select
                  value={formData.severity_level}
                  onValueChange={(value) => setFormData({ ...formData, severity_level: value })}
                >
                  <SelectTrigger data-testid="severity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t.safety.low}</SelectItem>
                    <SelectItem value="medium">{t.safety.medium}</SelectItem>
                    <SelectItem value="high">{t.safety.high}</SelectItem>
                    <SelectItem value="critical">{t.safety.critical}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t.safety?.incidentType || "Incident Type"}</Label>
                <Select
                  value={formData.incident_type || "__none__"}
                  onValueChange={(value) => setFormData({ ...formData, incident_type: value === "__none__" ? "" : value })}
                >
                  <SelectTrigger data-testid="incident-type-select">
                    <SelectValue placeholder={t.safety?.selectIncidentType || "Select type"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t.common?.notSpecified || "Not specified"}</SelectItem>
                    {INCIDENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {getIncidentTypeLabel(type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.safety.equipmentType}</Label>
                <Select
                  value={formData.equipment_type || "__none__"}
                  onValueChange={(value) => setFormData({ ...formData, equipment_type: value === "__none__" ? "" : value, equipment_involved: [] })}
                >
                  <SelectTrigger data-testid="equipment-type-select">
                    <SelectValue placeholder={t.safety.selectEquipmentType} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t.safety.notEquipmentRelated}</SelectItem>
                    <SelectItem value="aircraft">{t.flightOps.aircraft}</SelectItem>
                    <SelectItem value="battery">{t.fleet.batteries}</SelectItem>
                    <SelectItem value="other">{t.safety.other}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 flex items-end">
                <div className="flex items-center gap-2 p-3 rounded-lg border bg-amber-50 border-amber-200">
                  <Checkbox
                    id="reportable_to_ucl"
                    checked={formData.reportable_to_ucl}
                    onCheckedChange={(checked) => setFormData({ ...formData, reportable_to_ucl: checked })}
                    data-testid="reportable-ucl-checkbox"
                  />
                  <div>
                    <Label htmlFor="reportable_to_ucl" className="text-sm font-medium text-amber-800">
                      {t.safety?.reportableToUcl || "CAA Reporting Required"}
                    </Label>
                    <p className="text-xs text-amber-600">
                      {t.safety?.reportableToUclHint || "Must be reported to CAA"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {formData.equipment_type && formData.equipment_type !== "other" && (
              <div className="space-y-2">
                <Label>
                  {formData.equipment_type === "aircraft" ? t.safety.aircraftInvolved : t.safety.batteryInvolved}
                </Label>
                <Select
                  value={formData.equipment_involved[0] || "__none__"}
                  onValueChange={(value) => setFormData({ ...formData, equipment_involved: value === "__none__" ? [] : [value] })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.safety.selectEquipment} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t.safety.noneSelected}</SelectItem>
                    {formData.equipment_type === "aircraft" ? (
                      aircraft.map((ac) => (
                        <SelectItem key={ac.id} value={ac.id}>
                          <div className="flex items-center gap-2">
                            <Plane className="h-3 w-3" />
                            {ac.model} (S/N: {ac.serial_number})
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      batteries.map((bat) => (
                        <SelectItem key={bat.id} value={bat.id}>
                          <div className="flex items-center gap-2">
                            <Battery className="h-3 w-3" />
                            {bat.model} (S/N: {bat.serial_number})
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="description">{t.safety.description} *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                placeholder={t.safety.descriptionPlaceholder}
                required
                data-testid="incident-description"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="reporting_required"
                checked={formData.reporting_required}
                onCheckedChange={(checked) => setFormData({ ...formData, reporting_required: checked })}
                data-testid="reporting-required"
              />
              <Label htmlFor="reporting_required" className="cursor-pointer">
                {t.safety.reportingRequired}
              </Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="corrective_actions">{t.safety.correctiveActionsIfAny}</Label>
              <Textarea
                id="corrective_actions"
                value={formData.corrective_actions}
                onChange={(e) => setFormData({ ...formData, corrective_actions: e.target.value })}
                rows={2}
                placeholder={t.safety.correctiveActionsPlaceholder}
                data-testid="corrective-actions"
              />
            </div>

            {/* Attachments Section */}
            {!editingIncident && (
              <div className="space-y-3">
                <Label>{t.safety.attachments}</Label>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx"
                  />
                  <div className="flex flex-col items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="gap-2"
                    >
                      <Upload className="h-4 w-4" />
                      {t.safety.uploadFiles}
                    </Button>
                    <p className="text-xs text-slate-500">{t.safety.filesDescription}</p>
                  </div>
                  
                  {attachments.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {attachments.map((file, idx) => (
                        <div key={`attachment-${file.name}-${file.size}-${idx}`} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <div className="flex items-center gap-2">
                            {file.type.startsWith("image/") ? (
                              <Image className="h-4 w-4 text-slate-400" />
                            ) : (
                              <FileText className="h-4 w-4 text-slate-400" />
                            )}
                            <span className="text-sm truncate max-w-xs">{file.name}</span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeAttachment(idx)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>{t.actions.cancel}</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="incident-submit-btn">
                {editingIncident ? t.actions.update : t.safety.reportIncident}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.dialogs.resolveIncident}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="resolve_actions">{t.safety.correctiveActions} *</Label>
              <Textarea
                id="resolve_actions"
                value={correctiveActions}
                onChange={(e) => setCorrectiveActions(e.target.value)}
                rows={4}
                placeholder={t.safety.resolvePlaceholder}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveDialog(false)}>{t.actions.cancel}</Button>
            <Button onClick={handleResolve} className="bg-emerald-600 hover:bg-emerald-700">
              {t.actions.markAsResolved}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incident Detail Dialog with Attachments */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              {t.safety.incidents} - {viewingIncident?.incident_date ? formatDate(viewingIncident.incident_date) : ""}
            </DialogTitle>
          </DialogHeader>
          {viewingIncident && (
            <div className="space-y-6">
              {/* Incident Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-slate-500 block">{t.safety.missionReference}</span>
                  <div className="font-medium">
                    {viewingIncident.mission_reference_id ? (
                      <span className="font-mono text-sky-600">{viewingIncident.mission_reference_id}</span>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-slate-500 block">{t.safety.classification}</span>
                  <div className="font-medium">
                    {viewingIncident.incident_classification === "serious" ? (
                      <Badge className="bg-rose-100 text-rose-800">{t.safety.serious}</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800">{t.safety.minor}</Badge>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-slate-500 block">{t.safety.severity}</span>
                  <div className="font-medium">{getSeverityBadge(viewingIncident.severity_level)}</div>
                </div>
                <div>
                  <span className="text-sm text-slate-500 block">{t.safety?.incidentType || "Incident Type"}</span>
                  <div className="font-medium">{getIncidentTypeLabel(viewingIncident.incident_type)}</div>
                </div>
                <div>
                  <span className="text-sm text-slate-500 block">{t.common.status}</span>
                  <div className="font-medium">
                    {viewingIncident.resolution_status === "resolved" ? (
                      <Badge className="bg-emerald-100 text-emerald-800">{t.safety.resolved}</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800">{t.safety.open}</Badge>
                    )}
                  </div>
                </div>
                {viewingIncident.reportable_to_ucl && (
                  <div>
                    <span className="text-sm text-slate-500 block">{t.safety?.reportableToUcl || "CAA Reporting"}</span>
                    <Badge className="bg-rose-100 text-rose-800">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {t.safety?.reportableToUcl || "Required"}
                    </Badge>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <span className="text-sm text-slate-500 block mb-1">{t.safety.description}</span>
                <div className="text-sm bg-slate-50 p-3 rounded-lg">{viewingIncident.description}</div>
              </div>

              {/* Corrective Actions */}
              {viewingIncident.corrective_actions && (
                <div>
                  <span className="text-sm text-slate-500 block mb-1">{t.safety.correctiveActions}</span>
                  <div className="text-sm bg-emerald-50 p-3 rounded-lg">{viewingIncident.corrective_actions}</div>
                </div>
              )}

              {/* Attachments Section */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Paperclip className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">{t.safety.attachments}</span>
                  <Badge variant="outline" className="text-xs">
                    {viewingIncident.attachments?.length || 0}
                  </Badge>
                </div>
                
                {(!viewingIncident.attachments || viewingIncident.attachments.length === 0) ? (
                  <p className="text-sm text-slate-400 text-center py-4 bg-slate-50 rounded-lg">
                    {t.safety.noAttachments || "No attachments"}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {viewingIncident.attachments.map((attachment, idx) => (
                      <div 
                        key={attachment.id || idx} 
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {getFileIcon(attachment.mime_type)}
                          <div>
                            <p className="text-sm font-medium">{attachment.file_name}</p>
                            <p className="text-xs text-slate-500">
                              {attachment.mime_type} • {formatTimestamp(attachment.upload_timestamp)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isImageFile(attachment.mime_type) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePreviewImage(viewingIncident, attachment)}
                              title={t.actions.view}
                            >
                              <Eye className="h-4 w-4 text-sky-600" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownloadAttachment(viewingIncident, attachment)}
                            title={t.actions.download}
                          >
                            <Download className="h-4 w-4 text-slate-600" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Photos Section - Wave 3 Block 3C */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Camera className="h-4 w-4 text-sky-500" />
                    <span className="text-sm font-medium text-slate-700">{t.safety?.photos || "Photos"}</span>
                    <Badge variant="outline" className="text-xs">
                      {viewingIncident.photos?.length || 0}
                    </Badge>
                  </div>
                  <div>
                    <input
                      type="file"
                      ref={photoInputRef}
                      className="hidden"
                      accept="image/*"
                      multiple
                      onChange={(e) => handlePhotoUpload(e, viewingIncident.id)}
                    />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="gap-1"
                      onClick={() => photoInputRef.current?.click()}
                    >
                      <Upload className="h-3 w-3" />
                      {t.safety?.uploadPhoto || "Upload Photo"}
                    </Button>
                  </div>
                </div>
                
                {(!viewingIncident.photos || viewingIncident.photos.length === 0) ? (
                  <p className="text-sm text-slate-400 text-center py-4 bg-slate-50 rounded-lg">
                    {t.safety?.noPhotos || "No photos"}
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {viewingIncident.photos.map((photo) => (
                      <div 
                        key={photo.id} 
                        className="relative group rounded-lg border overflow-hidden bg-slate-100"
                      >
                        <div 
                          className="aspect-square cursor-pointer"
                          onClick={() => handlePreviewPhoto(viewingIncident, photo)}
                        >
                          <div className="w-full h-full flex items-center justify-center">
                            <Camera className="h-8 w-8 text-slate-300" />
                          </div>
                        </div>
                        
                        {/* Photo metadata overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          <div className="absolute bottom-0 left-0 right-0 p-2 text-white text-xs">
                            <p className="truncate font-medium">{photo.file_name}</p>
                            {(photo.exif_gps_lat && photo.exif_gps_lon) && (
                              <p className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {photo.exif_gps_lat.toFixed(4)}, {photo.exif_gps_lon.toFixed(4)}
                              </p>
                            )}
                            {photo.exif_timestamp && (
                              <p className="text-slate-300">{photo.exif_timestamp}</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Delete button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-1 right-1 h-6 w-6 bg-black/50 text-white opacity-0 group-hover:opacity-100 hover:bg-rose-600"
                          onClick={(e) => { e.stopPropagation(); handleDeletePhoto(viewingIncident, photo.id); }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Comments Section - Wave 3 Block 3C */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">{t.safety?.comments || "Comments"}</span>
                  <Badge variant="outline" className="text-xs">
                    {viewingIncident.comments?.length || 0}
                  </Badge>
                </div>
                
                {/* Add comment */}
                <div className="flex gap-2 mb-4">
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder={t.safety?.commentPlaceholder || "Write a comment..."}
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddComment(viewingIncident.id);
                      }
                    }}
                  />
                  <Button 
                    size="icon"
                    onClick={() => handleAddComment(viewingIncident.id)}
                    disabled={!newComment.trim() || submittingComment}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
                
                {(!viewingIncident.comments || viewingIncident.comments.length === 0) ? (
                  <p className="text-sm text-slate-400 text-center py-4 bg-slate-50 rounded-lg">
                    {t.safety?.noComments || "No comments"}
                  </p>
                ) : (
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {viewingIncident.comments.map((comment) => (
                      <div 
                        key={comment.id} 
                        className="p-3 bg-slate-50 rounded-lg border"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700">
                            {comment.user_name || comment.user_email}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">
                              {formatTimestamp(comment.created_at)}
                            </span>
                            {(comment.user_id === user?.id || user?.role === 'platform_admin' || user?.role === 'organization_admin') && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-slate-400 hover:text-rose-600"
                                onClick={() => handleDeleteComment(viewingIncident.id, comment.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-slate-600">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>{t.actions.close}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={showImagePreview} onOpenChange={(open) => {
        if (!open && previewImage?.url) {
          window.URL.revokeObjectURL(previewImage.url);
        }
        setShowImagePreview(open);
        if (!open) setPreviewImage(null);
      }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              {previewImage?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 flex items-center justify-center p-4 bg-slate-100 rounded-lg min-h-[400px]">
              {previewImage?.url && (
                <img 
                  src={previewImage.url} 
                  alt={previewImage.name}
                  className="max-w-full max-h-[60vh] object-contain rounded"
                />
              )}
            </div>
            
            {/* EXIF Metadata Panel */}
            {previewImage?.metadata && (previewImage.metadata.exif_gps_lat || previewImage.metadata.exif_timestamp || previewImage.metadata.exif_camera) && (
              <div className="lg:w-64 p-4 bg-slate-50 rounded-lg space-y-3">
                <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2">
                  <Camera className="h-4 w-4" />
                  {t.safety?.photoMetadata || "Photo Metadata"}
                </h4>
                
                {previewImage.metadata.exif_gps_lat && previewImage.metadata.exif_gps_lon && (
                  <div>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {t.safety?.exifLocation || "GPS Coordinates"}
                    </span>
                    <p className="text-sm font-mono">
                      {previewImage.metadata.exif_gps_lat.toFixed(6)}, {previewImage.metadata.exif_gps_lon.toFixed(6)}
                    </p>
                    <a 
                      href={`https://maps.google.com/?q=${previewImage.metadata.exif_gps_lat},${previewImage.metadata.exif_gps_lon}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-sky-600 hover:underline"
                    >
                      Open in Maps
                    </a>
                  </div>
                )}
                
                {previewImage.metadata.exif_timestamp && (
                  <div>
                    <span className="text-xs text-slate-500">{t.safety?.exifTimestamp || "Capture Time"}</span>
                    <p className="text-sm">{previewImage.metadata.exif_timestamp}</p>
                  </div>
                )}
                
                {previewImage.metadata.exif_camera && (
                  <div>
                    <span className="text-xs text-slate-500">{t.safety?.exifCamera || "Camera"}</span>
                    <p className="text-sm">{previewImage.metadata.exif_camera}</p>
                  </div>
                )}
                
                {previewImage.metadata.file_size_bytes && (
                  <div>
                    <span className="text-xs text-slate-500">{t.safety?.photoSize || "Size"}</span>
                    <p className="text-sm">{(previewImage.metadata.file_size_bytes / (1024 * 1024)).toFixed(2)} MB</p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImagePreview(false)}>{t.actions.close}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
